/**
 * IVA de la factura de venta con precios IVA incluido, con el mismo cálculo que el backend
 * (backend/src/ventas/iva-venta.ts): por cada alícuota, total = Σ subtotales de sus ítems,
 * neto = total / (1 + alícuota / 100) a centavos e IVA = total − neto, así Σ (neto + IVA) = total de la factura.
 */

import { centavos } from './comprobanteCompra';

export interface TotalAlicuotaVenta { alicuota: number; netoGravado: number; iva: number }

export interface ResumenIvaVenta {
  porAlicuota: TotalAlicuotaVenta[];
  subtotalNeto: number;
  montoIva: number;
  total: number;
}

function extraerIva(total: number, alicuota: number) {
  const t = centavos(Number(total));
  const neto = centavos(t / (1 + Number(alicuota) / 100));
  return { neto, iva: centavos(t - neto) };
}

/** Totales por alícuota (orden de primera aparición), extrayendo neto e IVA del total de cada alícuota. */
export function ivaIncluidoPorAlicuota(items: { alicuota: number; total: number }[]): TotalAlicuotaVenta[] {
  const totales = new Map<number, number>();
  for (const i of items) totales.set(Number(i.alicuota), centavos((totales.get(Number(i.alicuota)) ?? 0) + Number(i.total)));
  return [...totales].map(([alicuota, total]) => {
    const { neto, iva } = extraerIva(total, alicuota);
    return { alicuota, netoGravado: neto, iva };
  });
}

/**
 * Desglose de la factura. Con `total` (el de la OV) distinto de la suma de los ítems, la diferencia se reparte en
 * proporción al importe de cada alícuota (el resto de redondeo va a la mayor), igual que el backend.
 */
export function resumenIvaVenta(items: { alicuota: number; total: number }[], total?: number): ResumenIvaVenta {
  const sumaItems = centavos(items.reduce((s, i) => s + Number(i.total), 0));
  const totalFactura = total != null ? centavos(Number(total)) : sumaItems;
  const diferencia = centavos(totalFactura - sumaItems);
  let ajustados = items;
  if (diferencia !== 0 && items.length > 0) {
    // Se reparte la diferencia en proporción al importe de cada alícuota (el resto de redondeo va a la mayor):
    // con un total positivo ninguna alícuota queda negativa, aunque la diferencia sea grande (descuento global).
    const grupos = ivaIncluidoPorAlicuota(items).map(g => ({ alicuota: g.alicuota, total: centavos(g.netoGravado + g.iva) }));
    const escalados = grupos.map(gr => ({
      alicuota: gr.alicuota,
      total: sumaItems > 0 ? centavos(gr.total * totalFactura / sumaItems) : 0,
    }));
    const resto = centavos(totalFactura - escalados.reduce((s, e) => s + e.total, 0));
    const mayor = escalados.reduce((a, b) => (b.total > a.total ? b : a));
    mayor.total = centavos(mayor.total + resto);
    ajustados = escalados;
  }
  const porAlicuota = ivaIncluidoPorAlicuota(ajustados);
  return {
    porAlicuota,
    subtotalNeto: centavos(porAlicuota.reduce((s, a) => s + a.netoGravado, 0)),
    montoIva: centavos(porAlicuota.reduce((s, a) => s + a.iva, 0)),
    total: totalFactura,
  };
}

/** Alícuota como texto: "21 %", "10,5 %". */
export const etiquetaAlicuota = (a: number | string) => `${Number(a).toLocaleString('es-AR')} %`;
